import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRestaurante } from "../context/RestauranteContext";
import { useUser } from "../context/UserContext";
import { useNotificacao } from "../context/NotificacaoContext";
import "../css/Header.css";
import { FaBell } from "react-icons/fa";
import { FiLogOut, FiLogIn } from "react-icons/fi";
import image from "../assets/Gemini_Generated_Image_wjmb1gwjmb1gwjmb.png";
import { situacaoFuncionamento } from "../utils/horarioFuncionamento";

// componentes
import SidebarMenu from "./SidebarMenu";
import HamburgerButton from "./HamburgerButton";

const getStatusFuncionamento = (operatingHours) => {
  const situacao = situacaoFuncionamento(operatingHours);
  if (!situacao) return null;
  if (situacao.aberto) return "Aberto agora";
  if (!situacao.horarioHoje) return "Fechado hoje";
  return `Abre às ${situacao.horarioHoje.horarioAbertura}`;
};

const Header = () => {
  const { notificacoes, naoLidas, marcarComoLida, atualizarUserId } = useNotificacao();
  const { restaurantes, restauranteId, fetchRes } = useRestaurante();
  const { user, logout, userDecoded } = useUser();

  const [dropdownAberto, setDropdownAberto] = useState(false);

  const [restaurante, setRestaurante] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();


  const isRestaurantesPage = location.pathname.includes("/restaurantes");
  const isAdminPage = location.pathname.startsWith("/admin");
  const shouldShowBackButton = !isRestaurantesPage;

  // Sidebar / hamburger
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleHamburger = () => setMenuOpen((v) => !v);

  // lock scroll ao abrir o menu
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = menuOpen ? "hidden" : prev || "";
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (user) {
      const { userId } = userDecoded();
      if (userId) atualizarUserId(userId);
    }

    // Fora do cardápio, garante a lista de restaurantes (o cardápio carrega o próprio restaurante).
    if (!restauranteId || isRestaurantesPage) fetchRes();
  }, [isRestaurantesPage, restauranteId, user]);

  useEffect(() => {
    const restauranteAtual = restaurantes?.find((r) => r.id === Number(restauranteId));
    setRestaurante(restauranteAtual || null);
  }, [restaurantes, restauranteId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownAberto && !event.target.closest(".notificacao-wrapper")) {
        setDropdownAberto(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownAberto]);

  const handleNotificacaoClick = (notificacao) => {
    marcarComoLida(notificacao.id);
    setDropdownAberto(false);
    switch (notificacao.tipoNotificacao) {
      case "ROTINAS":
        navigate(`/restaurantes/${notificacao.restauranteId}/restaurantes/rotinas`, {
          state: { responsavelSelecionado: notificacao.responsavel, shouldReload: true },
        });
        break;
      case "APROVEITAMENTOS":
        navigate(`/restaurantes/${notificacao.restauranteId}/restaurantes/aproveitamentos`, {
          state: { responsavelSelecionado: notificacao.responsavel, shouldReload: true },
        });
        break;
      case "PRODUCAO":
        navigate(`/restaurantes/${notificacao.restauranteId}/restaurantes/producao`, {
          state: { responsavelSelecionado: notificacao.responsavel, shouldReload: true },
        });
        break;
      default:
        break;
    }
  };

  const handleToggleDropdown = (notificacao = null) => {
    if (notificacao?.id) {
      handleNotificacaoClick(notificacao);
    } else {
      setDropdownAberto(!dropdownAberto);
    }
  };

  const handleLogout = () => {
    navigate("/login");
    logout();
  };

  return (
    <header className={isAdminPage ? "header" : "header tema-cliente"}>
      {/* Sidebar */}
      <SidebarMenu open={menuOpen} onClose={() => setMenuOpen(false)} />

      {restaurante || isRestaurantesPage || isAdminPage ? (
        <>
          <div className="header-left">
            {/* botão hamburger como componente */}
            {user && <HamburgerButton open={menuOpen} onToggle={toggleHamburger} />}

            <img
              src={isRestaurantesPage || isAdminPage ? image : restaurante?.logoImage}
              alt={isRestaurantesPage || isAdminPage ? "Genius Menu" : `Logo de ${restaurante?.nome || "restaurante"}`}
              className="header-image"
            />
            <div className="header-info">
              <h1>{isRestaurantesPage || isAdminPage ? "Genius Menu" : restaurante?.nome}</h1>
              {!isRestaurantesPage && !isAdminPage && (
                <p className="info">
                  {getStatusFuncionamento(restaurante?.operatingHours) || "Horário não informado"} • Sem pedido mínimo
                </p>
              )}
            </div>
          </div>

          <div className="header-right">
            <div className="header-right-content">
              <div className="header-actions">

                {user && (
                  <div className="notificacao-wrapper">
                    <FaBell className="icon" onClick={() => handleToggleDropdown()} />
                    {naoLidas > 0 && <span className="notificacao-badge">{naoLidas}</span>}
                    {dropdownAberto && (
                      <div className="notificacao-dropdown">
                        <h3>Notificações</h3>
                        <div className="notificacao-conteudo">
                          {notificacoes.length === 0 ? (
                            <p className="sem-notificacoes">Nenhuma notificação disponível</p>
                          ) : (
                            <ul className="notificacao-lista">
                              {notificacoes.map((notificacao) => (
                                <li
                                  key={notificacao.id}
                                  className={notificacao.lida ? "" : "nao-lida"}
                                  onClick={() => handleToggleDropdown(notificacao)}
                                >
                                  <div className="notificacao-item">
                                    <p className="notificacao-titulo">
                                      {notificacao.titulo || "Notificação"}
                                    </p>
                                    <p className="notificacao-mensagem">{notificacao.mensagem}</p>
                                    <small>{new Date(notificacao.createdAt).toLocaleString()}</small>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {user ? (
                  <button className="logout-button" onClick={handleLogout}>
                    <FiLogOut size={20} />
                  </button>
                ) : (
                  <button className="logout-button" onClick={() => navigate("/login")}>
                    <FiLogIn size={20} />
                  </button>
                )}
              </div>

              {user && userDecoded()?.userName && (
                <div className="user-email-container">
                  <p className="user-email" title={userDecoded().userName}>
                    {userDecoded().userName}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </header>
  );
};

export default Header;
